import { useContext, useEffect, useCallback, useState, useMemo } from "react";
import "./Bookings.scss";
import { AuthContext } from "../../../contexts/AuthContext";
import PageTitle from "../../../components/PageTitle/PageTitle";
import AlertModal from "../../../components/AlertModal/AlertModal";
import { DynamicSelect } from "../../../components/Inputs/Inputs";
import Table from "../../../components/Table/Table";
import Pagination from "../../../components/Pagination/Pagination";

const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d);
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const yyyy = date.getUTCFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const diffNights = (checkin_date, checkout_date) => {
  if (!checkin_date || !checkout_date) return 0;
  const ci = new Date(checkin_date);
  const co = new Date(checkout_date);
  const ms = co - ci;
  if (Number.isNaN(ms)) return 0;
  const nights = Math.round(ms / (1000 * 60 * 60 * 24));
  return Math.max(0, nights);
};

export default function Bookings() {
  const { axiosAuth, accessibleGroups } = useContext(AuthContext);
  const [bookings, setBookings] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  // paginação
  const [page, setPage] = useState(1);
  const limit = 20;
  const [total, setTotal] = useState(0);
  const totalPages = useMemo(() => Math.ceil(total / limit), [total]);

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(() => {
    setErrorModal({ open: false, title: "", message: "" });
  }, []);

  const groupsOptions = useMemo(() => {
    const base = (accessibleGroups || []).map((g) => ({
      value: String(g.id),
      label: g.name,
    }));
    return base;
  }, [accessibleGroups]);

  const fetchBookings = useCallback(async () => {
    if (accessibleGroups.length === 0 || !selectedGroupId) {
      setBookings([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const params = {
        group_id: Number(selectedGroupId),
        skip: (page - 1) * limit,
        limit,
      };
      // const { data } = await axiosAuth.get(
      //   `/form-events/group/${selectedGroupId}`
      // );
      const { data } = await axiosAuth.get("/form-events/", { params });

      // estrutra: {results, total}
      if (
        data &&
        Array.isArray(data.results) &&
        typeof data.total === "number"
      ) {
        setBookings(data.results);
        setTotal(data.total);
        return;
      }
      // setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      openError(
        "Erro ao carregar reservas",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [axiosAuth, selectedGroupId, openError, page]);

  useEffect(() => {
    if (accessibleGroups.length > 1 && !selectedGroupId) {
      setSelectedGroupId(String(accessibleGroups[0].id));
    }
  }, [accessibleGroups, selectedGroupId]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchBookings();
    }
  }, [fetchBookings, selectedGroupId]);

  const columns = useMemo(() => [
    { key: "hotel_name", label: "Hotel" },
    { key: "checkin_date", label: "Check-in" },
    { key: "checkout_date", label: "Check-out" },
    { key: "nights", label: "Noites" },
    { key: "adults", label: "Adults" },
    { key: "children", label: "Crianças" },
    { key: "infants", label: "Bebés" },
    { key: "promocode", label: "Promocode" },
  ]);

  const tableData = useMemo(() =>
    bookings.map((b) => ({
      id: b.id,
      hotel_name: b.hotel_name,
      checkin_date: formatDate(b.checkin_date),
      checkout_date: formatDate(b.checkout_date),
      nights: diffNights(b.checkin_date, b.checkout_date),
      adults: b.adults,
      children: b.children,
      infants: b.infants,
      promocode: b.promocode,
    }))
  );
  return (
    <div className="bookings-page">
      {errorModal.open && (
        <AlertModal
          title={errorModal.title || "Ocorreu um erro"}
          variant="danger"
          message={errorModal.message}
          onClose={closeError}
        />
      )}
      <PageTitle title="Reservas" />
      <div className="filters">
        <DynamicSelect
          label="Cliente"
          placeholder="Escolher um grupo"
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          disabled={loading}
          options={groupsOptions}
        />
      </div>
      <div className="container">
        <Table columns={columns} data={tableData} />
      </div>
      {total > limit && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      )}
    </div>
  );
}
